const radix = 10;
const mobileRegex = /^\d{10}$/;
const binRegex = /^\d{6,22}$/;
const upperCase = /^[A-Z_]+$/;

/**
 * @private
 * card validation based on luhn algorithm
 * @param {Number | String} cn - card number
 * @return {Object} [output] - output object contains result of luhn algorithm
 * @return {Boolean} output.hasError - property set to true if enter card number has error
 * @return {Boolean} output.isCardNumberValid - property set to true if enter card number has error
 *
 * */
const _luhn = cn => {
  const output = {
    hasError: false, // no error occur
    isCardNumberValid: true // card number  has no error
  };
  const nDigits = cn.length;
  let nSum = 0;
  let isSecond = false;
  let isValid = false;

  for (let i = nDigits - 1; i >= 0; i -= 1) {
    let d = parseInt(cn[i], radix);
    if (isSecond === true) d *= 2;

    nSum += parseInt(d / 10, radix);
    nSum += d % 10;
    isSecond = !isSecond;
  }
  isValid = nSum % 10 === 0; // is valid
  if (!isValid) {
    output.hasError = true;
    output.isCardNumberValid = false; // card has failed luhn algo
  }
  return output;
};

/**
 * Validation on month and year of card
 * @param {Number | String} _month - month of card ( 1 ,2 etc )
 * @param {Number | String } _year - year of card  ( 2019 etc )
 * @return {Object} [output] - output contain result of running validations rules
 * @return {Boolean} output.hasError - field is set to true if contains error
 * @return {Boolean} output.isMonthValid - field is set to true if entered month is not valid
 * @return {Boolean} output.isYearValid - field is set to true if entered year is not valid
 * */
const _expiryValidation = (_month, _year) => {
  const date = new Date();
  let month = _month;
  let year = _year;
  const output = {
    hasError: false, // no error occur
    isMonthValid: true, // month has no error
    isYearValid: true // year has no error
  };
  year = parseInt(year, radix);
  month = parseInt(month, radix);

  if (year < date.getFullYear()) {
    // year is less then current year
    output.hasError = true;
    output.isYearValid = false;
    return output;
  }

  if (
    month < 1 ||
    month > 12 /* month is out of range 1 - 12 */ ||
    (year === date.getFullYear() &&
      month <
        date.getMonth() + 1) /* year is same as current year and month is less then current month */
  ) {
    output.hasError = true;
    output.isMonthValid = false;
    return output;
  }
  return output;
};

/**
 * Card validation
 * @param {Object} card - card object
 * @param {Number | String} card.month - month of card ( 1 ,2 etc )
 * @param {Number | String } card.year - year of card  ( 2019 etc )
 * @return {Object} [output] - output contain result of running validations rules
 * @return {Boolean} output.hasError - field is set to true if contains error
 * @return {Boolean} output.isMonthValid - field is set to true if entered month is not valid
 * @return {Boolean} output.isYearValid - field is set to true if entered year is not valid
 * */
export function isCardValid(card) {
  const output = {
    hasError: false, // no error occur
    isCardNumberValid: true, // card number has no error
    isMonthValid: true, // month has no error
    isYearValid: true // year has no error
  };

  let result = _luhn(card.number);
  if (result.hasError) {
    output.hasError = result.hasError;
    output.isCardNumberValid = result.isCardNumberValid;
    return output;
  }
  result = _expiryValidation(card.month, card.year);
  if (result.hasError) {
    output.hasError = result.hasError;
    output.isMonthValid = result.isMonthValid;
    output.isYearValid = result.isYearValid;
  }
  return output;
}

/**
 * Validate mobile number
 *
 * @param {number|string} mobileNumber - mobile number
 * @returns {boolean} - result of validation on mobile number
 */
export const validateMobile = mobileNumber => {
  return mobileRegex.test(mobileNumber);
};

/**
 * Validate card number
 *
 * @param {string} bin - card number
 * @returns {boolean} - result of validation on card number
 */
export const validateBin = bin => {
  return binRegex.test(bin);
};

/**
 * Validate uppercase input value
 *
 * @param {string} code - input value pass as uppercase.
 * @returns {boolean} - result of validation on input value.
 */
export const validateUpperCase = code => {
  return upperCase.test(code);
};



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/Preprod-BnR/Deploy_checkoutjs/common/src/utils/validations.js